import React, { useCallback, useMemo, useState } from 'react';
import { Menu, message } from 'antd';
import styled from 'styled-components';
import { generateMenuItems } from '../utils/generateMenuItems';
import type {
  EpiDocumentType,
  EpiPackagingBlock,
  EpiPredefinedEntry,
  EpiSectionNode,
} from '../types/types';
import { useEpiContext } from '../context/EpiContext';
import { useToggleVisibility } from '../../../hooks';
import ConfirmModal from '../../ConfirmModal';
import NatureTypeModal from './NatureTypeModal';
import { menuSurfaceStyles } from '../utils/menuSurfaceStyles';
import { sectionsPath } from '../utils/sectionTree';
import { getSchemaSectionAtPath, takesNatureType } from '../utils/schemaUtils';
import { setText, text } from '../utils/epiHelpers';
import { v4 as uuid } from 'uuid';
import { get } from 'lodash';

/** Quick-fill nature/type suggestions per repeatable Labelling key. */
const NATURE_TYPE_PRESETS: Record<string, string[]> = {
  outerInnerPackaging: [
    'Spoljašnje pakovanje',
    'Unutrašnje pakovanje',
    'Spoljašnje i unutrašnje pakovanje',
  ],
  blisterStrip: ['Blister', 'Strip'],
  smallPackaging: ['Bočica', 'Ampula'],
};

interface IProps {
  /** Only used to namespace antd menu keys across panels. */
  compIdx: number;
  compositionId: string;
  docType: EpiDocumentType;
  onClick: (node: EpiSectionNode, compId: string) => void;
}

const CompositionPanel: React.FC<IProps> = ({
  compIdx,
  compositionId,
  docType,
  onClick,
}) => {
  const {
    // The language being *authored*, not the admin panel's. Section names in
    // the add menu come from schema defaults, and a nature/type is written
    // into this language.
    lang,
    composition,
    sectionTree,
    selectedSection,
    selectedCompositionId,
    readOnly,
    canAddCustomSections,
    setSelectedComposition,
    setSelectedSection,
    addOptionalSection,
    updateSectionNatureType,
    addCustomSection,
    addPredefinedSection,
    keepCustomSection,
    addPackagingBlockAfter,
    removeSection,
    moveCustomSection,
  } = useEpiContext();

  const prefix = `${compIdx}-`;
  const firstPath = sectionTree[0]?.path;
  const firstKey = `${prefix}${firstPath}`;

  // Derived, not held: the section can be selected from outside this menu —
  // the breadcrumb does it — and a local copy simply never heard about it.
  // Only the open composition highlights, since the tree belongs to that one.
  const selectedPath = selectedSection?.path;
  const selectedKeys = useMemo(
    () =>
      selectedCompositionId === compositionId && selectedPath
        ? [`${prefix}${selectedPath}`]
        : [],
    [selectedCompositionId, compositionId, selectedPath, prefix]
  );

  const [openKeys, setOpenKeys] = useState<string[]>([firstKey]);
  const [sectionToDelete, setSectionToDelete] = useState<{
    path: string;
    key: string;
    isCustom: boolean;
    customSectionId?: string;
  } | null>(null);

  const {
    isVisible: isDeleteConfirmVisible,
    show: showDeleteConfirm,
    hide: hideDeleteConfirm,
  } = useToggleVisibility();

  const handleRemoveSection = useCallback(
    (
      path: string,
      key: string,
      isCustom: boolean,
      customSectionId?: string
    ) => {
      setSectionToDelete({ path, key, isCustom, customSectionId });
      showDeleteConfirm();
    },
    [showDeleteConfirm]
  );

  const handleDeleteConfirm = useCallback(() => {
    if (!sectionToDelete) {
      hideDeleteConfirm();
      return;
    }

    removeSection(
      sectionToDelete.path,
      sectionToDelete.isCustom,
      sectionToDelete.customSectionId
    );
    message.success('Section deleted.');

    if (sectionTree.length > 0) {
      const next = sectionTree[0];
      onClick(next, compositionId);
      setOpenKeys(keys => Array.from(new Set([...keys, prefix + next.path])));
    }

    setSectionToDelete(null);
    hideDeleteConfirm();
  }, [
    sectionToDelete,
    removeSection,
    hideDeleteConfirm,
    compositionId,
    sectionTree,
    onClick,
    prefix,
  ]);

  const handleItemClick = useCallback(
    (node: EpiSectionNode) => onClick(node, compositionId),
    [onClick, compositionId]
  );

  // Pending nature/type prompt: `parentPath` set = adding a block,
  // `editPath` set = relabelling an existing one.
  const [natureRequest, setNatureRequest] = useState<{
    key: string;
    title: string;
    /** Data path of the instance array, e.g. "sections.blisterStrip". */
    blocksPath: string;
    parentPath?: string;
    editPath?: string;
    current?: string;
  } | null>(null);

  // Nature/types already used by live sibling instances, so the modal can
  // reject duplicates. When relabelling, the block's own value doesn't count
  // as taken — nature/types are unique among live siblings.
  const takenNatureTypes = useMemo(() => {
    if (!natureRequest) return [];
    const blocks = get(composition, natureRequest.blocksPath) as
      | EpiPackagingBlock[]
      | undefined;
    // Nature/types are compared within the language being edited: the same
    // block can legitimately read "Blister" in English and "Blister" in
    // Serbian, but two blocks may not collide inside one language.
    return (blocks ?? [])
      .filter(block => !block.toBeDeleted && !!text(block.natureType, lang))
      .filter(block => text(block.natureType, lang) !== natureRequest.current)
      .map(block => text(block.natureType, lang));
  }, [natureRequest, composition, lang]);

  const selectNewSection = useCallback(
    (path: string, parentPath: string) => {
      setOpenKeys(prev => [...new Set([...prev, `${prefix}${parentPath}`])]);
      setSelectedComposition(compositionId);
      setSelectedSection({ path } as EpiSectionNode);
    },
    [prefix, compositionId, setSelectedComposition, setSelectedSection]
  );

  const handleAddOptional = useCallback(
    (parentPath: string, key: string, repeatable: boolean, title: string) => {
      // A packaging block needs a nature/type to distinguish it from sibling
      // instances — collect that first, then create the section. The patient
      // card has none.
      const def = getSchemaSectionAtPath(
        docType,
        `${sectionsPath(parentPath)}.${key}`
      );
      if (repeatable && def && takesNatureType(def)) {
        setNatureRequest({
          key,
          title,
          parentPath,
          blocksPath: `${sectionsPath(parentPath)}.${key}`,
        });
        return;
      }
      const newPath = addOptionalSection(parentPath, key);
      if (newPath) selectNewSection(newPath, parentPath);
    },
    [addOptionalSection, selectNewSection, docType]
  );

  const handleEditNatureType = useCallback((node: EpiSectionNode) => {
    setNatureRequest({
      key: node.key,
      title: node.title,
      blocksPath: node.path.replace(/\[\d+\]$/, ''),
      editPath: node.path,
      current: node.natureType,
    });
  }, []);

  const handleNatureConfirm = useCallback(
    (natureType: string) => {
      if (!natureRequest) return;
      const { parentPath, key, editPath } = natureRequest;
      if (editPath) {
        updateSectionNatureType(editPath, natureType);
      } else if (parentPath) {
        const newPath = addOptionalSection(
          parentPath,
          key,
          setText(undefined, lang, natureType)
        );
        if (newPath) selectNewSection(newPath, parentPath);
      }
      setNatureRequest(null);
    },
    [
      natureRequest,
      addOptionalSection,
      selectNewSection,
      updateSectionNatureType,
    ]
  );

  const handleAddCustom = useCallback(
    (parentPath: string, title: string) => {
      const id = uuid();
      const parentSection = get(composition, parentPath);
      const newIndex = parentSection?.customSections?.length ?? 0;

      addCustomSection(parentPath, id, title);
      selectNewSection(`${parentPath}.customSections[${newIndex}]`, parentPath);
    },
    [addCustomSection, selectNewSection, composition]
  );

  const handleAddPredefined = useCallback(
    (host: EpiSectionNode, entry: EpiPredefinedEntry) =>
      selectNewSection(addPredefinedSection(host, entry), host.path),
    [addPredefinedSection, selectNewSection]
  );

  const handleAddBlockAfter = useCallback(
    (blockPath: string, key: string) => {
      const newPath = addPackagingBlockAfter(blockPath, key);
      if (newPath) selectNewSection(newPath, 'root');
    },
    [addPackagingBlockAfter, selectNewSection]
  );

  return (
    <>
      <StyledMenu
        mode="inline"
        selectedKeys={selectedKeys}
        openKeys={openKeys}
        onOpenChange={keys => setOpenKeys(keys as string[])}
        style={{ height: '100%', borderRight: 0, background: 'transparent' }}
      >
        {generateMenuItems({
          nodes: sectionTree,
          docType,
          lang,
          onClick: handleItemClick,
          onAddOptional: handleAddOptional,
          onAddCustom: handleAddCustom,
          onAddPredefined: handleAddPredefined,
          keepCustomSection,
          onAddBlockAfter: handleAddBlockAfter,
          editNatureType: handleEditNatureType,
          moveCustomSection,
          removeSection: handleRemoveSection,
          prefix,
          readOnly,
          canAddCustomSections,
        })}
      </StyledMenu>

      <ConfirmModal
        isVisible={isDeleteConfirmVisible}
        onConfirm={handleDeleteConfirm}
        onCancel={hideDeleteConfirm}
        title={'Delete section'}
      >
        Are you sure you want to delete this section?
      </ConfirmModal>

      {natureRequest && (
        <NatureTypeModal
          isOpen
          sectionTitle={natureRequest.title}
          presets={NATURE_TYPE_PRESETS[natureRequest.key] ?? []}
          takenNatureTypes={takenNatureTypes}
          initialValue={natureRequest.current}
          mode={natureRequest.editPath ? 'edit' : 'add'}
          onConfirm={handleNatureConfirm}
          onCancel={() => setNatureRequest(null)}
        />
      )}
    </>
  );
};

const StyledMenu = styled(Menu)`
  ${menuSurfaceStyles}

  .ant-menu-item,
  .ant-menu-submenu-title {
    padding-left: 0 !important;
  }

  .ant-menu-submenu .ant-menu .ant-menu-item,
  .ant-menu-submenu .ant-menu .ant-menu-submenu-title {
    padding-left: 8px !important;
  }

  .ant-menu-submenu .ant-menu .ant-menu .ant-menu-item,
  .ant-menu-submenu .ant-menu .ant-menu .ant-menu-submenu-title {
    padding-left: 16px !important;
  }

  .ant-menu-submenu .ant-menu .ant-menu .ant-menu .ant-menu-item,
  .ant-menu-submenu .ant-menu .ant-menu .ant-menu .ant-menu-submenu-title {
    padding-left: 24px !important;
  }

  .ant-menu-submenu .ant-menu .ant-menu .ant-menu .ant-menu .ant-menu-item,
  .ant-menu-submenu
    .ant-menu
    .ant-menu
    .ant-menu
    .ant-menu
    .ant-menu-submenu-title {
    padding-left: 32px !important;
  }

  .ant-menu-submenu-title span svg {
    color: #000;
  }

  .ant-menu-submenu-arrow {
    position: absolute !important;
    right: 30px !important;
    top: 50% !important;
    transform: translateY(-50%) !important;
  }

  .ant-menu-item,
  .ant-menu-submenu-title {
    padding-right: 0px !important;
  }

  .anticon.anticon-more.ant-dropdown-trigger svg {
    color: #4b576a;
  }
`;

export default CompositionPanel;
